import {
  Box,
  Button,
  InputGroup as ChakraInputGroup,
  FormControl,
  Grid,
  Input,
  InputLeftElement,
  InputRightElement,
  Select,
  Stack,
} from '@chakra-ui/react'
import { FormProvider, useFieldArray, useForm } from 'BrokerTsLib/src/rhf'

import { ConfirmRemoveButton } from '../../components/ConfirmRemoveButton'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { InputGroup } from '../../components/InputGroup'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { MainContent } from '../../components/MainContent'
import { Radio } from '../../components/Radio'
import { RadioGroup } from '../../components/RadioGroup'
import { RadioStack } from '../../components/RadioStack'
import { Add, Calendar, Check } from '../../icons'
import { smeCompanies } from '../../utils/companies'
import type { SmeBusinessAreaValues } from '../../utils/smeBusinessAreaValues'
import type { SmeInsuranceTypeValues } from '../../utils/smeInsuranceTypes'
import { AdditionalInsureeSmeInfo } from '../AdditionalInsureeSmeInfo'
import { FormButtons } from '../FormButtons'
import { InsuranceType } from './InsuranceType'
import { SmeBusinessAreaSelector } from './SmeBusinessAreaSelector'

interface Step1FormProps {
  onSubmit: Function
}

export interface Step1FormValues {
  projectName: string
  businessArea: SmeBusinessAreaValues['businessArea']
  businessAreaDomain: string
  insuranceType: SmeInsuranceTypeValues['insuranceType']
  insuranceLevel: 'lower' | 'medium' | 'higher'
  participation: string
  startDate: string
  contractType: string
  paymentFrequency: string
  paymentMethod: string
  venues: {
    houseStreet: string
    houseHouseNumber: string
    housePostalCode: string
    houseCity: string
    constructionType: string
    floorsAboveGround: string
    builtUpArea: string
    renewalPrice: string
    goodTechnicalConditions: string
  }[]
  stockValue: number
  vehicleEquipmentValue: number
  totalIncome: number
  employeeCount: number
  buildingType: string
  buildingState: string
  propertyValue: string
  insureeType: string
  businessId: string
  firstName: string
  lastName: string
  postalCode: string
  country: string
  city: string
}

const defaultVenue = {
  houseStreet: '',
  houseHouseNumber: '',
  housePostalCode: '',
  houseCity: '',
  constructionType: '1',
  floorsAboveGround: '1',
  builtUpArea: '',
  renewalPrice: '',
  goodTechnicalConditions: 'yes',
}

export const Step1Form = ({ onSubmit }: Step1FormProps) => {
  const methods = useForm<Step1FormValues>({
    defaultValues: {
      businessArea: null,
      businessAreaDomain: '',
      insuranceType: null,
      insuranceLevel: 'lower',
      contractType: 'signature',
      paymentFrequency: '1',
      paymentMethod: '1',
      venues: [defaultVenue],
      buildingType: '2',
      buildingState: '1',
      insureeType: '1',
    },
  })
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
  } = methods

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'venues', // unique name for your Field Array
  })

  const onSubmitForm = (values: Step1FormValues) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        onSubmit(values)
        resolve(true)
      }, 500)
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Oblast podnikání</FormSectionTitle>

              <FormSectionDescription>Vyberte, které věci budou pojištěny.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody sx={{ w: '100%' }}>
              <SmeBusinessAreaSelector />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Parametry pojištění</FormSectionTitle>

              <FormSectionDescription>Zadejte základní parametry pojištění.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              {/* Project name */}
              <FormControl isInvalid={!!errors['projectName']}>
                <FormLabel htmlFor="projectName" optional>
                  Název nabídky
                </FormLabel>
                <InputGroup
                  id="projectName"
                  placeholder="Zadejte název nabídky"
                  htmlSize={32}
                  errors={errors['projectName']}
                  {...register('projectName')}
                />
              </FormControl>

              <FormSpacer />

              <InsuranceType />

              <FormSpacer />

              <FormControl>
                <FormLabel>Rozsah pojištění</FormLabel>

                <RadioGroup control={control} name="insuranceLevel">
                  <RadioStack variant="stackedTile">
                    <Radio value="lower">Nižší</Radio>
                    <Radio value="medium">Střední</Radio>
                    <Radio value="higher">Vyšší</Radio>
                  </RadioStack>
                </RadioGroup>
              </FormControl>

              <FormSpacer />

              <FormGroup>
                {/* Date */}
                <FormControl>
                  <FormLabel htmlFor="startDate">Datum počátku pojištění</FormLabel>

                  <ChakraInputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      children={<Calendar width={4} height={4} />}
                    />
                    <Input id="startDate" type="date" {...register('startDate')} />
                  </ChakraInputGroup>
                </FormControl>

                {/* Contract type */}
                <FormControl>
                  <FormLabel>Způsob sjednání smlouvy</FormLabel>

                  <RadioGroup control={control} name="contractType">
                    <RadioStack variant="stackedTile">
                      <Radio value="signature">Podpisem</Radio>
                      <Radio value="payment">Zaplacením</Radio>
                    </RadioStack>
                  </RadioGroup>
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormGroup size="lg">
                <FormControl>
                  <FormLabel htmlFor="paymentFrequency">Frekvence plateb</FormLabel>

                  <RadioGroup control={control} name="paymentFrequency">
                    <Stack direction="column" spacing={4}>
                      <Radio value="1">Roční</Radio>
                      <Radio value="2">Pololetní</Radio>
                      <Radio value="3">Čtvrtletní</Radio>
                      <Radio value="4">Měsíční</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="paymentMethod">Způsob platby</FormLabel>

                  <RadioGroup control={control} name="paymentMethod">
                    <Stack direction="column" spacing={4}>
                      <Radio value="1">Převodem z účtu</Radio>
                      <Radio value="2">Složenkou</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <InsuranceCompanies companies={smeCompanies} isLoadingCompanies={false} propPath='insurers' />
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Místo pojištění</FormSectionTitle>

              <FormSectionDescription>Zadejte základní údaje o nemovitosti.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <Box sx={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
                {fields.map((field, index) => {
                  return (
                    <Grid
                      sx={{
                        gap: 4,
                        p: 4,
                        justifyItems: 'start',
                        bg: 'gray.100',
                        borderRadius: 'md',
                      }}
                      key={field.id}
                    >
                      {index > 0 && <ConfirmRemoveButton onClick={() => remove(index)} />}

                      <FormGroup>
                        <FormControl>
                          <FormLabel>Ulice</FormLabel>

                          <InputGroup
                            htmlSize={32}
                            placeholder="Např. Hlavní"
                            {...register(`venues.${index}.houseStreet`)}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Číslo popisné nebo orientační</FormLabel>

                          <InputGroup
                            htmlSize={8}
                            placeholder="Např. 1234"
                            {...register(`venues.${index}.houseHouseNumber`)}
                          />
                        </FormControl>
                      </FormGroup>

                      <FormGroup>
                        <FormControl>
                          <FormLabel tooltip="PSČ">PSČ</FormLabel>

                          <InputGroup
                            htmlSize={6}
                            placeholder="Např. 110 00"
                            {...register(`venues.${index}.housePostalCode`)}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel tooltip="Obec">Obec</FormLabel>

                          <Select {...register(`venues.${index}.houseCity`)}>
                            <option disabled value="">
                              Vyberte obec
                            </option>
                          </Select>
                        </FormControl>
                      </FormGroup>

                      <FormSpacer />

                      <FormControl>
                        <FormLabel tooltip="Typ konstrukce">Typ konstrukce</FormLabel>

                        <Select {...register(`venues.${index}.constructionType`)}>
                          <option disabled value="">
                            Vyberte typ konstrukce
                          </option>
                          <option value="1">Zděná</option>
                          <option value="2">Dřevěná</option>
                          <option value="3">Smíšená</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel tooltip="Počet nadzemních podlaží">
                          Počet nadzemních podlaží
                        </FormLabel>

                        <InputGroup
                          maxW={32}
                          placeholder="Např. 1"
                          {...register(`venues.${index}.floorsAboveGround`)}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel tooltip="Zastavěná plocha">Zastavěná plocha</FormLabel>

                        <ChakraInputGroup maxW={32}>
                          <Input
                            placeholder="Např. 128"
                            {...register(`venues.${index}.builtUpArea`)}
                          />
                          <InputRightElement children="㎡" />
                        </ChakraInputGroup>
                      </FormControl>

                      <FormControl>
                        <FormLabel tooltip="Obnovovací cena budovy">
                          Obnovovací cena budovy
                        </FormLabel>

                        <ChakraInputGroup maxW={48}>
                          <Input
                            placeholder="Např: 50 000 000"
                            {...register(`venues.${index}.renewalPrice`)}
                          />
                          <InputRightElement children="Kč" />
                        </ChakraInputGroup>
                      </FormControl>

                      <FormControl>
                        <FormLabel tooltip="Dobrý technický stav">Dobrý technický stav</FormLabel>

                        <RadioGroup
                          control={control}
                          name={`venue.${index}.goodTechnicalConditions`}
                        >
                          <RadioStack variant="stackedTile">
                            <Radio value="yes">Ano</Radio>
                            <Radio value="no">Ne</Radio>
                          </RadioStack>
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )
                })}

                <Button type="button" variant="outline" onClick={() => append(defaultVenue)}>
                  <Add color="secondary.500" />
                  Přidat další adresu
                </Button>
              </Box>
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Doplňující informace o pojištění</FormSectionTitle>

              <FormSectionDescription>Zadejte doplňující informace o firmě.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <FormControl>
                <FormLabel tooltip="Hodnota zásob">Hodnota zásob</FormLabel>

                <ChakraInputGroup maxW={48}>
                  <Input placeholder="Např: 50 000 000" {...register('stockValue')} />
                  <InputRightElement children="Kč" />
                </ChakraInputGroup>
              </FormControl>

              <FormControl>
                <FormLabel tooltip="Hodnota strojů a vybavení">Hodnota strojů a vybavení</FormLabel>

                <ChakraInputGroup maxW={48}>
                  <Input placeholder="Např: 50 000 000" {...register('vehicleEquipmentValue')} />
                  <InputRightElement children="Kč" />
                </ChakraInputGroup>
              </FormControl>

              <FormControl>
                <FormLabel tooltip="Celkový příjem">Celkový příjem</FormLabel>

                <ChakraInputGroup maxW={48}>
                  <Input placeholder="Např: 50 000 000" {...register('totalIncome')} />
                  <InputRightElement children="Kč" />
                </ChakraInputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Počet zaměstnanců</FormLabel>

                <ChakraInputGroup maxW={32}>
                  <Input placeholder="Např: 100" {...register('employeeCount')} />
                </ChakraInputGroup>
              </FormControl>
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>Zadejte informace o pojistníkovi.</FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <AdditionalInsureeSmeInfo hideContacts />
            </FormSectionBody>
          </FormSection>

          <FormButtons
            rightButtons={
              <>
                <Button variant="outline" disabled={isSubmitting} type="button">
                  Uložit
                </Button>
                <Button
                  colorScheme="primary"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  loadingText="Ukládám..."
                  type="submit"
                >
                  <Check />
                  Uložit a pokračovat
                </Button>
              </>
            }
          />
        </MainContent>
      </form>
    </FormProvider>
  )
}
